import { useRef, useState } from 'react';
import { NJButton, NJInlineMessage, NJText, NJToastService } from '@engie-group/fluid-design-system-react';
import { downloadRatioTemplate, parseRatioFile, parseRatioWorkbook, type ImportResult } from './importRatios';
import type { DegreeDays, RatioTable as Ratios } from './types';

interface Props {
  degrees: DegreeDays;
  ratios: Ratios;
  onImport: (degrees: DegreeDays, ratios: Ratios) => void;
  disabled?: boolean;
  /** Masque le rappel de format (boutons alignés avec les autres actions). */
  compact?: boolean;
}

type Note = { tone: 'warning' | 'error'; text: string; detail?: string[] };

/** Import d'un fichier de ratios (export tableur) et modèle pré-rempli. */
export function RatioImport({ degrees, ratios, onImport, disabled, compact }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState<Note | null>(null);
  const [reading, setReading] = useState(false);

  const read = async (file: File) => {
    setReading(true);
    setNote(null);
    let r: ImportResult;
    try {
      r = /\.xlsx?$/i.test(file.name)
        ? parseRatioWorkbook(await file.arrayBuffer(), { degrees, ratios })
        : parseRatioFile(await file.text(), { degrees, ratios });
    } catch (error) {
      setNote({ tone: 'error', text: `Impossible de lire « ${file.name} » : ${error instanceof Error ? error.message : 'fichier illisible'}. Utilisez le modèle Excel proposé.` });
      return;
    } finally {
      setReading(false);
    }
    if (r.applied === 0 && r.coefficientsApplied === 0 && !r.djuFound && !r.djclimFound) {
      setNote({ tone: 'error', text: `Aucune donnée reconnue dans « ${file.name} ». Partez du modèle pour retrouver le format attendu.`, detail: r.skipped.slice(0, 4) });
      return;
    }
    onImport(r.degrees, r.ratios);
    const parts = [`${r.applied} ratio${r.applied > 1 ? 's' : ''} importé${r.applied > 1 ? 's' : ''}`];
    if (r.coefficientsApplied) parts.push(`${r.coefficientsApplied} coefficients repris`);
    if (r.djuFound) parts.push('DJU repris');
    if (r.djclimFound) parts.push('DJClim repris');
    if (r.skipped.length) parts.push(`${r.skipped.length} ligne(s) ignorée(s)`);
    if (r.skipped.length) {
      setNote({ tone: 'warning', text: parts.join(' · '), detail: r.skipped.slice(0, 4) });
    } else {
      NJToastService(parts.join(' · '), { title: 'Import terminé', iconName: 'check_circle',
        dismissAfter: 6000, hasCloseIcon: true, gaugeLabel: 'Cette confirmation se ferme après 6 secondes.' });
    }
  };

  const download = () => {
    try {
      downloadRatioTemplate(degrees, ratios);
    } catch (error) {
      setNote({ tone: 'error', text: `Le modèle Excel n’a pas pu être généré : ${error instanceof Error ? error.message : 'erreur de téléchargement'}.` });
    }
  };

  return (
    <div className="pcv3-ratio-import">
      <div className="pcv3-ratios-input-actions">
        <NJButton variant="secondary" icon="download" disabled={disabled || reading} onClick={download} label="Télécharger le modèle pré-rempli" />
        <NJButton variant="secondary" icon="upload_file" disabled={disabled || reading} onClick={() => fileRef.current?.click()}
          label={reading ? 'Lecture du fichier…' : 'Importer un fichier'} />
        {!compact && <NJText scale="sm">Modèle Excel : Paramètres (DJU/DJClim de référence), Chauffage, ECS et Froid. Anciens Excel, CSV et TSV acceptés.</NJText>}
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.tsv,.txt,.xlsx,.xls"
          disabled={disabled || reading}
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void read(f); e.target.value = ''; }}
        />
      </div>
      {note && (
        <NJInlineMessage variant={note.tone} role="alert">
          <NJText>{note.text}</NJText>
          {note.detail && note.detail.length > 0 && <NJText scale="sm">Non reprises : {note.detail.join(' · ')}</NJText>}
        </NJInlineMessage>
      )}
    </div>
  );
}
